// public/v3/src/lib/prompt-text.js — shared render-time neutralization for
// chart free text interpolated into AI prompt assembly (care-plan +
// billing-note builders). Generalizes the pain-section fix from the
// 2026-07-21 adversarial review (PR #617): staff/Elation-authored text must
// not be able to close a prompt section tag (</bhi_section>) or open a fake
// one (<instructions>) and impersonate prompt structure.
//
// Two neutralization semantics, chosen per site:
//
// - promptSafeLine / promptSafeBlock degrade ONLY tag-shaped brackets — a
//   '<' immediately followed by an optional '/' and a tag-name start
//   character — to U+2039 '‹'. Clinical comparisons ("BP <140/90",
//   "HbA1c <7%", "K+ > 5.0") are everywhere in visit notes, lab trends and
//   prior plans; stripping every bracket would silently change clinical
//   meaning in a signed document's source data, which is worse than the
//   injection it prevents. A bare '>' cannot open structure without a
//   matching '<name', so it is left alone.
//
// - stripPromptAngles strips ALL angle brackets — the original #617
//   semantics, kept byte-identical for careplan-pain.js's already-shipped
//   fields (short staff-authored strings where comparisons are rare).
//
// Render-time only: stored records are never rewritten. Pure functions,
// CommonJS so Jest can require this directly (the ESM builders import it
// via interop, same pattern as careplan-bhi.js).

// '<' begins structure only when followed by optional '/' then a tag-name
// start char (letter, or '!'/'?' for comments/PIs). '<140' is data.
const TAG_OPEN_RE = /<(?=\/?[A-Za-z!?])/g;

function _neutralizeTags(text) {
  return String(text == null ? '' : text).replace(TAG_OPEN_RE, '‹');
}

// Single-line interpolation (list rows, labels, names, dx strings):
// tag-neutralize, collapse control chars (a newline inside a "- date: ..."
// row would fabricate a new row), trim, optional cap.
function promptSafeLine(text, cap) {
  const t = _neutralizeTags(text)
    .replace(/[\x00-\x1f\x7f]+/g, ' ')
    .trim();
  return cap && t.length > cap ? t.slice(0, cap) : t;
}

// Multi-line interpolation (visit notes, lab trends, prior plans): the
// text's own line structure is meaningful — keep \n (and \t), normalize
// CRLF, drop other control chars, tag-neutralize, optional cap.
function promptSafeBlock(text, cap) {
  const t = _neutralizeTags(text)
    .replace(/\r\n?/g, '\n')
    .replace(/[\x00-\x08\x0b-\x1f\x7f]+/g, ' ')
    .trim();
  return cap && t.length > cap ? t.slice(0, cap) : t;
}

// Original pain-section semantics (PR #617) — strips every angle bracket.
// careplan-pain.js keeps these byte-identical; new sites should prefer the
// meaning-preserving variants above.
function stripPromptAngles(text, cap) {
  const t = String(text == null ? '' : text).replace(/[<>]/g, '').trim();
  return cap && t.length > cap ? t.slice(0, cap) : t;
}

module.exports = {
  promptSafeLine,
  promptSafeBlock,
  stripPromptAngles,
};
